import { useBreakpoint } from '@/hooks/useBreakpoint';
import { Separator, SizableText, XStack, YStack } from 'tamagui';
import { StepBox, StepTitleText } from './stepIndicator.styled';

/**
 * Props for the StepIndicator component.
 * Defines the interface for a generic multi-step progress indicator.
 */
export interface StepIndicatorProps {
  /** Total number of steps in the process. */
  totalSteps: number;
  /**
   * The current active step (1-indexed).
   * Should be between 1 and totalSteps.
   */
  currentStep: number;
  /**
   * Array of step titles to display.
   * On small screens: only the current step title is displayed at the bottom.
   * On larger screens: all step titles are displayed above their respective steps.
   */
  stepTitles: string[];
}

/**
 * Generic StepIndicator component for multi-step forms.
 * Displays progress through a sequence of steps with visual indicators.
 *
 * Features:
 * - Displays steps as horizontal rectangles
 * - Highlights current step with brand color
 * - Shows completed/future steps in gray
 * - Responsive title display (bottom on small screens, top on large screens)
 * - Reusable across different forms
 *
 * @example
 * <StepIndicator
 *   totalSteps={4}
 *   currentStep={2}
 *   stepTitles={["General Info", "Surveillance", "Diagnostic", "Documents"]}
 * />
 */
export const StepIndicator = ({
  totalSteps,
  currentStep,
  stepTitles,
}: StepIndicatorProps): React.ReactElement => {
  const { isSmallScreen } = useBreakpoint();

  return (
    <YStack gap="$5" width="100%">
      <XStack gap="$2" width="100%" justifyContent="space-between">
        {Array.from({ length: totalSteps }, (_, index) => {
          const stepNumber = index + 1;
          const isActive = stepNumber <= currentStep;

          return (
            <YStack key={stepNumber} flex={1} gap="$2">
              {/* Title above each step on large screens */}
              {!isSmallScreen && (
                <StepTitleText
                  size="$2"
                  fontFamily="$bold"
                  color={isActive ? '$color' : '$secondary25'}
                >
                  {stepTitles[index]}
                </StepTitleText>
              )}
              <StepBox isActive={isActive} />
            </YStack>
          );
        })}
      </XStack>

      {/* Title below indicator on small screens */}
      {isSmallScreen && (
        <YStack gap="$true">
          <SizableText size="$h6" fontFamily="$bold">
            {stepTitles[currentStep - 1]}
          </SizableText>
          <Separator backgroundColor="$color" />
        </YStack>
      )}
    </YStack>
  );
};
